import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Modal, 
  TextField, 
  Button, 
  Divider 
} from '@mui/material';
import { 
  footerStyle, 
  headerStyle, 
  inputStyle, 
  modalStyle 
} from '../OrderEntry/styles';
import { NumericFormat } from 'react-number-format';
import { tokens } from '../../Common/designTokens'; // added

const PriceCommentModal = ({ 
  open, 
  onClose, 
  selectedRow, 
  rungIndex, 
  onSubmit 
}) => {
  const [price, setPrice] = useState('');
  const [comment, setComment] = useState('');
  const [qty, setQty] = useState(''); // new state for qty

  // Reset fields when a new row is selected or modal opens
  useEffect(() => {
    if (selectedRow && open) {
      setPrice(selectedRow.price || '');
      setComment(selectedRow.comments || '');
      // Prefill the input with the current stored (user-edited) qty if available,
      // otherwise fall back to originalQty (the unedited value).
      setQty(
        (selectedRow.qty !== undefined && selectedRow.qty !== null)
          ? selectedRow.qty
          : ((selectedRow.originalQty !== undefined && selectedRow.originalQty !== null)
              ? selectedRow.originalQty
              : '')
      );
    }
  }, [selectedRow, open]);

  const handleSubmit = () => {
    if (!selectedRow) return;

    // Determine final qty value; always use the value user provided in modal (or fallback to selectedRow.qty)
    const finalQty = (qty === '' || qty === undefined || qty === null) ? selectedRow.qty : Number(qty);

    // Determine if qty was changed compared to the current stored order (selectedRow.qty)
    const qtyChanged = selectedRow && (Number(selectedRow.qty || 0) !== Number(finalQty));

    const payload = {
      ...selectedRow,
      price: price === '' || price === undefined ? selectedRow.price : price,
      comments: comment,
      qty: finalQty,
      manualQty: Boolean(qtyChanged), // flag to indicate manual qty override
    };

    onSubmit(payload);
    onClose();
  };

  // format numbers with thousand separators, fallback to '---'
  const fmtNum = (v) => {
    if (v === null || v === undefined || v === '') return '---';
    const n = Number(v);
    if (Number.isNaN(n)) return '---';
    return n.toLocaleString('en-US');
  };

  // format price rounded to 5 decimal places, fallback to '---'
  const fmtPrice5 = (v) => {
    if (v === null || v === undefined || v === '') return '---';
    const n = Number(v);
    if (Number.isNaN(n)) return '---';
    return n.toFixed(5);
  };
  
  if (!selectedRow) return null;
  
  return (
    <Modal 
      open={open} 
      onClose={onClose} 
      aria-labelledby="price-comment-modal-title"
    >
      <Box sx={{
        ...modalStyle,
        width: "700px",
        bgcolor: tokens.colors.bodyBackgroundColor,
        border: `1px solid ${tokens.colors.bodyBorderColor50}`,
        borderRadius: "8px",
      }}>
        <Box sx={headerStyle}>
          <Typography 
            id="price-comment-modal-title" 
            variant="h6" 
            fontWeight="bold"
            sx={{ color: tokens.colors.fontColour }}
          >
          Modify Rung {parseInt(rungIndex) + 1}
          </Typography>
        </Box>
        
        {/* Row Details with Vertical Separators */}
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'space-between', 
          p: 2,
          bgcolor: tokens.colors.defaultGridRowColor,
          my: 2,
          mx: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontWeight: 'bold', color: tokens.colors.headerFontColour }}>
              CUSIP: {selectedRow.cusip}
            </Typography>
            
            <Divider orientation="vertical" sx={{ mx: 2, backgroundColor: tokens.colors.bodyBorderColor50, height: 20 }} />
            
            <Typography sx={{ fontWeight: 'bold', color: tokens.colors.headerFontColour }}>
              Action: {selectedRow.side || selectedRow.action }
            </Typography>
            
            <Divider orientation="vertical" sx={{ mx: 2, backgroundColor: tokens.colors.bodyBorderColor50, height: 20 }} />
            
            {/* Show ORIGINAL qty in the modal header (read-only), formatted with commas */}
            <Typography sx={{ fontWeight: 'bold', color: tokens.colors.headerFontColour }}>
              Qty: {fmtNum(selectedRow.originalQty ?? selectedRow.qty)}
            </Typography>
            
            <Divider orientation="vertical" sx={{ mx: 2, backgroundColor: tokens.colors.bodyBorderColor50, height: 20 }} />
            
            <Typography sx={{ fontWeight: 'bold', color: tokens.colors.headerFontColour }}>
              Min Qty: {fmtNum(selectedRow.minQty)}
            </Typography>
            
            <Divider orientation="vertical" sx={{ mx: 2, backgroundColor: tokens.colors.bodyBorderColor50, height: 20 }} />
            
            <Typography sx={{ fontWeight: 'bold', color: tokens.colors.headerFontColour }}>
              Inventory Price: {fmtPrice5(selectedRow.originalPrice ?? selectedRow.price)}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ 
          pt: 1, 
          pl: 3, 
          pr: 3,
          bgcolor: tokens.colors.defaultGridRowColor
        }}>
          {/* Price Input */}
          <NumericFormat
            customInput={TextField}
            autoFocus
            fullWidth
            label="Enter Price"
            value={price}
            variant="filled"
            placeholder="Enter Price"
            thousandSeparator=","
            allowNegative={false}
            decimalScale={5}
            InputProps={{
              disableUnderline: true
            }}
            sx={{
              ...inputStyle,
              mb: 2,
              mt: 1,
              '& .MuiInputBase-input::placeholder': {
                color: tokens.colors.fontColour25,
                opacity: 1
              },
              '& .MuiFilledInput-input:-webkit-autofill': {
                WebkitBoxShadow: `0 0 0 1000px ${tokens.colors.white} inset`,
                WebkitTextFillColor: tokens.colors.fontColour
              }
            }}
            onValueChange={(values) => {
              const { floatValue } = values;
              if (floatValue === undefined) {
                setPrice('');
                return;
              }
              setPrice(floatValue);
            }}
            isAllowed={(values) => {
              const { floatValue } = values;
              if (floatValue === undefined) return true;
              return floatValue <= 150;
            }}
          />
          
          {/* Quantity Input (new) */}
          <NumericFormat
            customInput={TextField}
            fullWidth
            label="Enter Quantity"
            value={qty}
            variant="filled"
            placeholder="Enter Quantity"
            thousandSeparator=","
            allowNegative={false}
            decimalScale={0}
            InputProps={{
              disableUnderline: true
            }}
            sx={{
              ...inputStyle,
              mb: 2,
              mt: 1,
              '& .MuiInputBase-input::placeholder': {
                color: tokens.colors.fontColour25,
                opacity: 1
              }
            }}
            onValueChange={(values) => {
              const { value, floatValue } = values;
              // if cleared
              if (value === '' || floatValue === undefined) {
                setQty('');
                return;
              }
              setQty(Math.trunc(floatValue));
            }}
            isAllowed={(values) => {
              const { floatValue } = values;
              if (floatValue === undefined) return true;
              // disallow negative and extremely large values (safety)
              return floatValue >= 0 && floatValue <= 1000000000;
            }}
          />
          
          {/* Comment Input */}
          <TextField
            fullWidth
            label="Enter Comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            multiline
            variant="filled"
            placeholder="Enter Comment"
            inputProps={{ maxLength: 200 }}
            InputProps={{
              disableUnderline: true,
              style: { height: '147px' }
            }}
            
            sx={{
              ...inputStyle,
              mb: 0.5, // Reduced margin to make space for character counter
              mt: 1,
              '& .MuiInputBase-root': {
                height: '147px',
              },
              '& .MuiInputBase-input': {
                height: '107px !important', // Account for label space
                overflow: 'auto !important',
                color: tokens.colors.fontColour
              },
              '& .MuiInputBase-input::placeholder': {
                color: tokens.colors.fontColour25,
                opacity: 1
              },
              "& .MuiFilledInput-root": {
                ...(inputStyle?.["& .MuiFilledInput-root"] || {}),
                paddingLeft: "0px",
              },
            }}
          />
          
          {/* Character counter */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            mb: 1.5,
            mt: 0.5
          }}>
            <Typography variant="caption" sx={{ 
              color: comment.length >= 180 ? tokens.palette.primary : tokens.colors.fontColour25,
              fontWeight: comment.length >= 180 ? 'bold' : 'normal'
            }}>
              {comment.length}/200 characters
            </Typography>
          </Box>
          
          <Box sx={{ mt: 3, mx: -3 }}>
            <Divider sx={{ borderColor: tokens.colors.bodyBorderColor50 }} />
          </Box>
          
          <Box sx={{
            ...footerStyle,
            borderTop: "none",
            bgcolor: tokens.colors.defaultGridRowColor,
            "& > :not(style) + :not(style)": {
              ml: 2,
            },
          }}>
            <Button 
              variant="outlined" 
              onClick={onClose} 
              sx={{ 
                textTransform: "none", 
                px: 4, 
                py: 1,
               
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{ 
                textTransform: "none", 
                px: 4, 
                py: 1,
                backgroundColor: tokens.palette.primary,
                color: tokens.colors.white,
                '&:hover': {
                  backgroundColor: tokens.palette.primaryA80,
                }
              }}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default PriceCommentModal;
